export type SearchSurfaceMode = 'quick' | 'advanced';

/**
 * - `idle`: `shown` is visible and matches `active`.
 * - `morphing`: the frame resizes to `active` while `leaving` fades out.
 * - `waiting`: the frame has the size of `active`, whose content is still
 *   loading; the animated logo covers the gap.
 */
export type SearchSurfacePhase = 'idle' | 'morphing' | 'waiting';

export interface SearchSurfaceState {
  active: SearchSurfaceMode | null;
  shown: SearchSurfaceMode | null;
  leaving: SearchSurfaceMode | null;
  phase: SearchSurfacePhase;
}

export type SearchSurfaceAction =
  | { type: 'target'; mode: SearchSurfaceMode | null; ready: boolean }
  | { type: 'morph-end'; ready: boolean }
  | { type: 'ready' };

export const initialSearchSurfaceState: SearchSurfaceState = {
  active: null,
  shown: null,
  leaving: null,
  phase: 'idle',
};

export function reduceSearchSurface(
  state: SearchSurfaceState,
  action: SearchSurfaceAction
): SearchSurfaceState {
  switch (action.type) {
    case 'target': {
      if (action.mode === state.active) return state;
      // Closing keeps the visible content so the frame fades out with it.
      if (action.mode === null) {
        return { ...state, active: null, leaving: null, phase: 'idle' };
      }
      if (state.active === null) {
        return {
          active: action.mode,
          shown: action.ready ? action.mode : null,
          leaving: null,
          phase: action.ready ? 'idle' : 'waiting',
        };
      }
      return {
        active: action.mode,
        shown: null,
        leaving: state.shown ?? state.leaving,
        phase: 'morphing',
      };
    }
    case 'morph-end':
      if (state.phase !== 'morphing') return state;
      return action.ready
        ? { ...state, shown: state.active, leaving: null, phase: 'idle' }
        : { ...state, shown: null, leaving: null, phase: 'waiting' };
    case 'ready':
      if (state.phase !== 'waiting') return state;
      return { ...state, shown: state.active, phase: 'idle' };
  }
}

/** Modes whose content stays mounted: target, fading-out and closing view. */
export function mountedSearchSurfaceModes(
  state: SearchSurfaceState
): SearchSurfaceMode[] {
  const modes = new Set<SearchSurfaceMode>();
  for (const mode of [state.active, state.leaving, state.shown]) {
    if (mode) modes.add(mode);
  }
  return [...modes];
}
